import React, { useState, useEffect } from 'react';
import { AppHeader } from '../../components/ui/layout/app-header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { 
  Paintbrush, 
  Building2, 
  Image as ImageIcon, 
  Save, 
  Trash2, 
  Palette, 
  UserCircle, 
  Settings, 
  LogOut,
  PlusCircle,
  Link,
  Ticket,
  Plane,
  LayoutDashboard,
  ClipboardCheck
} from 'lucide-react';

export default function BrandingPage() {
  const navigate = useNavigate();
  
  // State management
  const [name, setName] = useState('InvoiceCraft');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  // Load admin user and branding data on component mount
  useEffect(() => {
    // Get admin user from localStorage
    const adminData = localStorage.getItem('admin_user');
    if (adminData) {
      setAdminUser(JSON.parse(adminData));
    } else {
      // For demo purposes, set default admin user
      setAdminUser({
        username: 'SuperAdmin',
        email: 'admin@revynox.com',
        role: 'SuperAdmin'
      });
    }

    // Simulate API call to load existing branding
    const loadBranding = async () => {
      setIsLoading(true);
      // Simulate delay
      setTimeout(() => {
        // Load from localStorage or use defaults
        const savedName = localStorage.getItem('company_name') || 'InvoiceCraft';
        const savedLogo = localStorage.getItem('company_logo');
        
        setName(savedName);
        if (savedLogo) {
          setLogo(savedLogo);
          setLogoPreview(savedLogo);
        }
        setIsLoading(false);
      }, 1000);
    };

    loadBranding();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('admin_user');
    navigate('/');
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        setLogo(result);
        setLogoPreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoPreview(null);
  };

  const showToast = (message) => {
    // Simple toast implementation
    const toastDiv = document.createElement('div');
    toastDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50';
    toastDiv.textContent = message;
    document.body.appendChild(toastDiv);
    
    setTimeout(() => {
      document.body.removeChild(toastDiv);
    }, 3000);
  };

  const handleSaveChanges = () => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      // Save to localStorage (replace with actual API call)
      localStorage.setItem('company_name', name);
      if (logo) {
        localStorage.setItem('company_logo', logo);
      } else {
        localStorage.removeItem('company_logo');
      }
      
      setIsLoading(false);
      showToast('Branding updated successfully!');
    }, 1500);
  };

  // Avatar component replacement
  const Avatar = ({ children, className = "" }) => (
    <div className={`rounded-full overflow-hidden bg-gray-100 flex items-center justify-center ${className}`}>
      {children}
    </div>
  );

  const AvatarImage = ({ src, alt, className = "" }) => {
    if (!src) return null;
    return (
      <img 
        src={src} 
        alt={alt} 
        className={`w-full h-full object-cover ${className}`}
      />
    );
  };

  const AvatarFallback = ({ children }) => (
    <div className="text-gray-500">
      {children}
    </div>
  );

  // Profile dropdown component
  const ProfileDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <UserCircle className="h-6 w-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{adminUser?.username || 'SuperAdmin'}</p>
            <p className="text-xs text-muted-foreground">{adminUser?.email || 'admin@revynox.com'}</p>
            <p className="text-xs text-muted-foreground">Role: {adminUser?.role || 'SuperAdmin'}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/admin/settings')}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <AppHeader title="Branding & Customization">
        <div className="flex items-center gap-4">
        </div>
      </AppHeader>

      <main className="flex-1 p-4 md:p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold font-headline mb-2 flex items-center">
          <Palette className="mr-3 h-8 w-8 text-primary" />
          Branding & Customization
        </h1>
         <p className="text-muted-foreground">
          Customize the appearance of client-facing portals with your company's branding.
        </p>
        </div>

        {/* Main Content Card */}
        <Card className="max-w-2xl mx-auto shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Paintbrush className="mr-2 h-5 w-5 text-primary"/> 
              Customize Portals
            </CardTitle>
            <CardDescription>
              Changes made here will apply to the ticket submission and leave management portals.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoading ? (
              <div className="space-y-6">
                <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-24 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-10 w-32 bg-gray-200 rounded animate-pulse ml-auto"></div>
              </div>
            ) : (
              <>
                {/* Company Name Section */}
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="flex items-center font-semibold">
                    <Building2 className="mr-2 h-4 w-4" />
                    Company Name
                  </Label>
                  <Input
                    id="companyName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your Company Inc."
                  
                  />
                </div>

                {/* Company Logo Section */}
                <div className="space-y-2">
                  <Label htmlFor="logoUpload" className="flex items-center font-semibold">
                    <ImageIcon className="mr-2 h-4 w-4" />
                    Company Logo
                  </Label>
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16 border">
                      <AvatarImage src={logoPreview} alt="Company Logo Preview" />
                      <AvatarFallback><Building2  /></AvatarFallback>
                    </Avatar>
                    <div className="flex-grow">
                      <Input 
                        id="logoUpload" 
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
  className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                 />
                    </div>
                    {logoPreview && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={handleRemoveLogo}
                      >
                        <Trash2 className="h-5 w-5  text-destructive" />
                        <span className="sr-only">Remove Logo</span>
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Recommended: PNG or JPG format, maximum 2MB, square aspect ratio for best results.
                  </p>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleSaveChanges} 
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </>
  );
}